import { jsPDF } from "jspdf";

// ---------------------------------------------------------------------------
// Card-sheet PDF export.
//
// Every card is rasterised to an offscreen canvas whose pixel aspect ratio
// exactly matches the chosen physical card size, then placed into the PDF at
// that same physical size. Because the canvas and the PDF cell share one
// aspect ratio, the artwork is never stretched or squashed — no distortion.
// Rendering happens at 300 DPI so prints stay crisp.
// ---------------------------------------------------------------------------

const MM_PER_IN = 25.4;

// Physical card presets (width × height in millimetres). Every preset fits on
// a single side of US Letter (215.9 × 279.4 mm) in at least one orientation.
export const CARD_SIZES = [
  { key: "playden", label: "Playden Card (56 × 88 mm)", w: 56, h: 88 },
  { key: "tcg", label: "Trading Card (2.5 × 3.5 in)", w: 63.5, h: 88.9 },
  { key: "poker", label: "Poker (2.5 × 3.5 in)", w: 63.5, h: 88.9 },
  { key: "bridge", label: "Bridge (2.25 × 3.5 in)", w: 57.15, h: 88.9 },
  { key: "mini", label: "Mini / Yu-Gi-Oh (59 × 86 mm)", w: 59, h: 86 },
  { key: "tarot", label: "Tarot (2.75 × 4.75 in)", w: 69.85, h: 120.65 },
  { key: "jumbo", label: "Jumbo (3.5 × 5 in)", w: 88.9, h: 127 },
  { key: "business_us", label: "Business Card US (3.5 × 2 in)", w: 88.9, h: 50.8 },
  { key: "business_eu", label: "Business Card EU (85 × 55 mm)", w: 85, h: 55 },
  { key: "a8", label: "A8 (52 × 74 mm)", w: 52, h: 74 },
  { key: "a7", label: "A7 (74 × 105 mm)", w: 74, h: 105 },
  { key: "a6", label: "A6 (105 × 148 mm)", w: 105, h: 148 },
  { key: "a5", label: "A5 (148 × 210 mm)", w: 148, h: 210 },
];

// Paper presets (width × height in millimetres, portrait).
export const PAPER_SIZES = [
  { key: "letter", label: 'US Letter (8.5 × 11 in)', w: 215.9, h: 279.4 },
  { key: "a4", label: "A4 (210 × 297 mm)", w: 210, h: 297 },
  { key: "legal", label: "US Legal (8.5 × 14 in)", w: 215.9, h: 355.6 },
];

export const mmToIn = (mm) => mm / MM_PER_IN;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

// Replicate the app's card framing (object-fit + object-position + transform
// scale) onto a canvas sized to the target card's physical aspect ratio.
function renderCardCanvas(img, card, cardWmm, cardHmm, dpi, background, overlay, mirror) {
  const pxPerMm = dpi / MM_PER_IN;
  const cw = Math.max(1, Math.round(cardWmm * pxPerMm));
  const ch = Math.max(1, Math.round(cardHmm * pxPerMm));
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");

  if (background && background !== "transparent") {
    ctx.fillStyle = background === "black" ? "#000000" : "#ffffff";
    ctx.fillRect(0, 0, cw, ch);
  }

  // Heat-press mirror: flip everything (art + labels) horizontally so it reads
  // correctly once transferred. Applied for the rest of the drawing.
  if (mirror) {
    ctx.translate(cw, 0);
    ctx.scale(-1, 1);
  }

  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const fit = card?.image_fit || "cover";
  const base = fit === "cover" ? Math.max(cw / iw, ch / ih) : Math.min(cw / iw, ch / ih);
  const dispW = iw * base;
  const dispH = ih * base;
  const px = (card?.image_pos_x ?? 50) / 100;
  const py = (card?.image_pos_y ?? 50) / 100;
  const posX = (cw - dispW) * px;
  const posY = (ch - dispH) * py;

  // transform: scale(s) scales the box content around its centre.
  const s = card?.image_scale ?? 1;
  const cx = cw / 2;
  const cy = ch / 2;
  const fx = cx + (posX - cx) * s;
  const fy = cy + (posY - cy) * s;
  const fw = dispW * s;
  const fh = dispH * s;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, cw, ch);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, fx, fy, fw, fh);
  ctx.restore();

  if (overlay?.show) drawCardLabels(ctx, card, cw, ch, pxPerMm, overlay.bleedMm || 0);

  return canvas;
}

// Neon colour per rarity (canvas-safe comma HSL). Mirrors the app's --label.
export const RARITY_NEON = {
  common: "hsl(210, 10%, 86%)",
  uncommon: "hsl(160, 70%, 70%)",
  rare: "hsl(195, 92%, 62%)",
  epic: "hsl(265, 92%, 75%)",
  legendary: "hsl(42, 96%, 62%)",
  goddess: "hsl(210, 20%, 98%)",
};

// Rounded-rect path (ctx.roundRect isn't in every canvas impl).
function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// Semi-transparent rarity tag (top-left) + card name (bottom border), both
// glowing neon in the card's rarity colour. Kept inside the trim area so the
// bleed never crops them.
function drawCardLabels(ctx, card, cw, ch, pxPerMm, bleedMm) {
  const inset = bleedMm * pxPerMm;
  const pad = Math.max(6, ch * 0.028);
  const neon = RARITY_NEON[card?.rarity] || "#ffffff";
  const rarity = String(card?.rarity || "").toUpperCase();
  const name = String(card?.title || "").trim();

  ctx.save();
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  try { ctx.letterSpacing = `${Math.max(1, ch * 0.006)}px`; } catch { /* older canvas */ }

  // Rarity — top-left, semi-transparent neon with a soft bubble behind it.
  if (rarity) {
    const rSize = Math.max(9, Math.round(ch * 0.034));
    ctx.font = `700 ${rSize}px "Helvetica Neue", Arial, sans-serif`;
    const tw = ctx.measureText(rarity).width;
    const bx = inset + pad;
    const by = inset + pad;
    const padX = rSize * 0.55;
    const padY = rSize * 0.38;
    const bw = tw + padX * 2;
    const bh = rSize + padY * 2;
    const r = bh * 0.42;
    // Bubble: dark translucent fill + faint neon outline so the wording pops.
    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = "rgba(6, 10, 16, 1)";
    roundRectPath(ctx, bx, by, bw, bh, r);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = Math.max(1, rSize * 0.06);
    ctx.strokeStyle = neon;
    roundRectPath(ctx, bx, by, bw, bh, r);
    ctx.stroke();
    ctx.restore();
    // Neon text on top.
    ctx.shadowColor = neon;
    ctx.shadowBlur = rSize * 0.55;
    ctx.globalAlpha = 1;
    ctx.fillStyle = neon;
    ctx.fillText(rarity, bx + padX, by + padY);
    ctx.shadowBlur = 0;
  }

  // Edition n/N — top-right, matching the rarity bubble style.
  const edNum = card?.edition_number;
  const edCount = card?.edition_count;
  if (edNum && edCount) {
    const eText = `${edNum}/${edCount}`;
    const eSize = Math.max(9, Math.round(ch * 0.034));
    ctx.font = `700 ${eSize}px "Helvetica Neue", Arial, sans-serif`;
    const tw = ctx.measureText(eText).width;
    const padX = eSize * 0.55;
    const padY = eSize * 0.38;
    const bw = tw + padX * 2;
    const bh = eSize + padY * 2;
    const bx = cw - inset - pad - bw;
    const by = inset + pad;
    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = "rgba(6, 10, 16, 1)";
    roundRectPath(ctx, bx, by, bw, bh, bh * 0.42);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = Math.max(1, eSize * 0.06);
    ctx.strokeStyle = neon;
    roundRectPath(ctx, bx, by, bw, bh, bh * 0.42);
    ctx.stroke();
    ctx.restore();
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.shadowColor = neon;
    ctx.shadowBlur = eSize * 0.55;
    ctx.globalAlpha = 1;
    ctx.fillStyle = neon;
    ctx.fillText(eText, bx + padX, by + padY);
    ctx.shadowBlur = 0;
  }

  // Claim code — bottom, in an OPAQUE high-contrast box (goes under a scratch-off
  // sticker, so it must read cleanly once revealed). Uses a clear monospace font.
  const code = String(card?.code || "").trim();
  let nameBottomY = ch - inset - pad * 0.7;
  if (code) {
    const cSize = Math.max(10, Math.round(ch * 0.044));
    try { ctx.letterSpacing = `${Math.max(1, ch * 0.004)}px`; } catch { /* noop */ }
    ctx.font = `700 ${cSize}px "Courier New", "Roboto Mono", monospace`;
    const ctw = ctx.measureText(code).width;
    const cpadX = cSize * 0.6;
    const cpadY = cSize * 0.42;
    const maxBoxW = cw - 2 * (inset + pad * 0.6);
    const cbw = Math.min(maxBoxW, ctw + cpadX * 2);
    const cbh = cSize + cpadY * 2;
    const cbx = (cw - cbw) / 2;
    const cby = ch - inset - pad * 0.5 - cbh;
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#05070c";
    roundRectPath(ctx, cbx, cby, cbw, cbh, cbh * 0.26);
    ctx.fill();
    ctx.lineWidth = Math.max(1.2, cSize * 0.08);
    ctx.strokeStyle = neon;
    roundRectPath(ctx, cbx, cby, cbw, cbh, cbh * 0.26);
    ctx.stroke();
    ctx.shadowColor = neon;
    ctx.shadowBlur = cSize * 0.5;
    ctx.fillStyle = neon;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(code, cw / 2, cby + cbh / 2 + cSize * 0.06, maxBoxW - cpadX);
    ctx.restore();
    nameBottomY = cby - pad * 0.35; // lift the name above the code box
  }

  // Name — bottom border (lifted above the code box when present).
  if (name) {
    const nSize = Math.max(8, Math.round(ch * 0.03));
    try { ctx.letterSpacing = `${Math.max(1, ch * 0.006)}px`; } catch { /* noop */ }
    ctx.font = `600 ${nSize}px "Helvetica Neue", Arial, sans-serif`;
    const maxW = cw - 2 * (inset + pad);
    let text = name;
    while (text.length > 4 && ctx.measureText(text).width > maxW) {
      text = text.slice(0, -2);
    }
    if (text !== name) text = text.replace(/\s+\S*$/, "") + "…";
    // 25% black bubble behind the name so light rarities (common/goddess) stay legible.
    const ntw = Math.min(maxW, ctx.measureText(text).width);
    const nbPadX = nSize * 0.5;
    const nbPadY = nSize * 0.32;
    const nbw = ntw + nbPadX * 2;
    const nbh = nSize + nbPadY * 2;
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = "#000000";
    roundRectPath(ctx, (cw - nbw) / 2, nameBottomY - nbh, nbw, nbh, nbh * 0.4);
    ctx.fill();
    ctx.restore();
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.shadowColor = neon;
    ctx.shadowBlur = nSize * 0.6;
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = neon;
    ctx.fillText(text, cw / 2, nameBottomY - nbPadY * 0.6, maxW);
  }
  ctx.restore();
}

// Corner crop marks just outside each card, to guide cutting.
function drawCropMarks(pdf, x, y, w, h, len) {
  pdf.setDrawColor(150);
  pdf.setLineWidth(0.12);
  const corners = [
    [x, y, -1, -1],
    [x + w, y, 1, -1],
    [x, y + h, -1, 1],
    [x + w, y + h, 1, 1],
  ];
  for (const [cx, cy, sx, sy] of corners) {
    pdf.line(cx, cy, cx + sx * len, cy); // horizontal tick
    pdf.line(cx, cy, cx, cy + sy * len); // vertical tick
  }
}

// Resolve a card's printed footprint (mm) from the size's edge lengths and the
// chosen orientation mode. "match" honours each card's own orientation.
function itemDims(card, shortE, longE, mode) {
  const landscape = mode === "landscape" || (mode === "match" && card?.orientation === "landscape");
  return landscape ? { w: longE, h: shortE } : { w: shortE, h: longE };
}

// Row-packing plan: fills left→right, wraps rows, paginates. Cards keep their
// own footprint, so mixed portrait/landscape sheets stay tidy. Returns an
// array of pages; each page is a list of { card, w, h, x, y } placements
// relative to the content origin (top-left, inside the margin).
function planPages(items, usableW, usableH, gap) {
  const pages = [];
  let cur = [];
  let x = 0, y = 0, rowH = 0;
  const flushPage = () => { if (cur.length) pages.push(cur); cur = []; x = 0; y = 0; rowH = 0; };
  for (const it of items) {
    if (x > 0 && x + it.w > usableW + 1e-6) { x = 0; y += rowH + gap; rowH = 0; } // wrap row
    if (y + it.h > usableH + 1e-6 && cur.length) { flushPage(); }                 // new page
    cur.push({ ...it, x, y });
    x += it.w + gap;
    rowH = Math.max(rowH, it.h);
  }
  if (cur.length) pages.push(cur);
  return pages;
}

/**
 * Build and download a print-ready card sheet PDF. Each card is printed at the
 * selected physical size in its own orientation (unless forced).
 * @returns {Promise<{pages:number, count:number}>}
 */
export async function generateCardSheetPdf({
  cards,
  cardW,
  cardH,
  paperW,
  paperH,
  gap = 4,
  margin = 8,
  cropMarks = true,
  cardBorder = false,
  background = "white",
  orientationMode = "match",
  bleed = 0,
  labels = false,
  mirror = false,
  copies = 1,
  filename = "playden-card-sheet.pdf",
  dpi = 300,
  onProgress,
}) {
  const orientation = paperW > paperH ? "landscape" : "portrait";
  const pdf = new jsPDF({
    orientation,
    unit: "mm",
    format: [Math.min(paperW, paperH), Math.max(paperW, paperH)],
  });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const usableW = pageW - 2 * margin;
  const usableH = pageH - 2 * margin;
  const shortE = Math.min(cardW, cardH);
  const longE = Math.max(cardW, cardH);
  const b = Math.max(0, bleed);

  // Expand copies. `tw/th` = trim (final cut) size; `w/h` = footprint incl. bleed.
  const items = [];
  for (const c of cards) {
    const d = itemDims(c, shortE, longE, orientationMode);
    for (let i = 0; i < copies; i++) items.push({ card: c, tw: d.w, th: d.h, w: d.w + 2 * b, h: d.h + 2 * b });
  }

  // A card (incl. bleed) must physically fit the usable area in at least one placement.
  if (items.some((it) => it.w > usableW + 1e-6 || it.h > usableH + 1e-6)) {
    throw new Error("A card is too large for this paper. Reduce the card size, bleed or margins.");
  }

  const pages = planPages(items, usableW, usableH, gap);
  const markLen = Math.min(3, Math.max(1.5, gap * 0.9 || 2));
  const cache = new Map();
  let placed = 0;

  for (let p = 0; p < pages.length; p++) {
    if (p > 0) pdf.addPage();
    for (const it of pages[p]) {
      const x = margin + it.x;          // footprint (bleed box) top-left
      const y = margin + it.y;
      const tx = x + b;                  // trim (cut line) top-left
      const ty = y + b;
      const src = it.card.front_image;
      if (src) {
        try {
          let img = cache.get(src);
          if (!img) {
            img = await loadImage(src);
            cache.set(src, img);
          }
          // Render the artwork into the full bleed box so it runs past the cut line.
          const canvas = renderCardCanvas(img, it.card, it.w, it.h, dpi, background, { show: labels, bleedMm: b }, mirror);
          const useJpeg = background !== "transparent";
          const data = canvas.toDataURL(useJpeg ? "image/jpeg" : "image/png", 0.95);
          pdf.addImage(data, useJpeg ? "JPEG" : "PNG", x, y, it.w, it.h, undefined, "FAST");
        } catch {
          // Skip unreadable art but still draw the cut frame so the slot is usable.
        }
      }
      if (cardBorder) {
        pdf.setDrawColor(200);
        pdf.setLineWidth(0.15);
        pdf.rect(tx, ty, it.tw, it.th);
      }
      // Crop marks sit at the TRIM corners — cut here, bleed absorbs any drift.
      if (cropMarks) drawCropMarks(pdf, tx, ty, it.tw, it.th, markLen);
      placed++;
      onProgress?.(placed, items.length);
    }
  }

  pdf.save(filename);
  return { pages: pages.length, count: items.length };
}

// Layout math used for live feedback in the dialog (no rendering).
export function computeLayout({ cards = [], cardW, cardH, paperW, paperH, gap = 4, margin = 8, orientationMode = "match", bleed = 0, copies = 1 }) {
  const usableW = paperW - 2 * margin;
  const usableH = paperH - 2 * margin;
  const shortE = Math.min(cardW, cardH);
  const longE = Math.max(cardW, cardH);
  const b = Math.max(0, bleed);
  const list = cards.length ? cards : [{ orientation: orientationMode === "landscape" ? "landscape" : "portrait" }];
  const items = [];
  for (const c of list) {
    const d = itemDims(c, shortE, longE, orientationMode);
    for (let i = 0; i < (copies || 1); i++) items.push({ w: d.w + 2 * b, h: d.h + 2 * b });
  }
  const fits = !items.some((it) => it.w > usableW + 1e-6 || it.h > usableH + 1e-6);
  if (!fits) return { fits: false, pages: 0, firstPageCount: 0 };
  const pages = planPages(items, usableW, usableH, gap);
  return { fits: true, pages: pages.length, firstPageCount: pages[0]?.length || 0 };
}

